import React, { useEffect, useMemo, useState } from "react";
import axios from "../../utils/axiosInstance";
import { CalendarBlank, Warning, Download, Trash, CheckCircle } from "@phosphor-icons/react";

const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

export default function SwietaSubotaTab({ user }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [holidays, setHolidays] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // { swieto_date, name }
  const [form, setForm] = useState({ employee_id: "", wolny_date: "", data_wystawienia: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [h, e] = await Promise.all([
        axios.get(`/api/wnioski-urlop/saturday-holidays?year=${year}`),
        axios.get("/api/employees"),
      ]);
      setHolidays(h.data || []);
      setEmployees(e.data || []);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [year]);

  const addOdbior = async () => {
    if (!selected || !form.employee_id || !form.wolny_date) return;
    setSaving(true);
    try {
      await axios.post("/api/wnioski-urlop/odbior-dnia", {
        employee_id: form.employee_id,
        swieto_date: selected.swieto_date,
        wolny_date: form.wolny_date,
        data_wystawienia: form.data_wystawienia,
      });
      setForm({ employee_id: "", wolny_date: "", data_wystawienia: new Date().toISOString().slice(0, 10) });
      setSelected(null);
      await load();
    } catch (e) {
      alert("Błąd: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const removeOdbior = async (id) => {
    if (!window.confirm("Usunąć?")) return;
    try {
      await axios.delete(`/api/wnioski-urlop/odbior-dnia/${id}`);
      await load();
    } catch (e) { alert("Błąd: " + (e.response?.data?.detail || e.message)); }
  };

  const downloadPdf = async (id, name, sw) => {
    try {
      const res = await axios.get(`/api/wnioski-urlop/odbior-dnia/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `odbior_${name}_${sw}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert("Błąd: " + e.message); }
  };

  return (
    <div className="space-y-4" data-testid="swieta-sobota-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold">Święta w sobotę — odbiór dnia wolnego</h3>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}
          className="px-3 py-1.5 border border-border rounded-md text-sm bg-white" data-testid="swieta-year">
          {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900 flex gap-2">
        <Warning className="w-5 h-5 shrink-0" />
        <div>
          <div className="font-semibold">Art. 130 § 2 Kodeksu pracy</div>
          <div>Jeżeli święto państwowe przypada w sobotę, pracownikom zatrudnionym w pełnym wymiarze przysługuje inny dzień wolny — który należy im wyznaczyć.</div>
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-sm text-muted-foreground">Ładowanie...</div>
      ) : holidays.length === 0 ? (
        <div className="p-6 bg-white border border-border rounded-md text-sm text-muted-foreground">
          Brak świąt w sobotę w {year} roku.
        </div>
      ) : (
        <div className="space-y-3">
          {holidays.map((h) => (
            <div key={h.swieto_date} className="bg-white border border-border rounded-md p-4 shadow-sm" data-testid={`swieto-${h.swieto_date}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-md bg-red-50 flex items-center justify-center">
                    <CalendarBlank className="w-6 h-6 text-red-600" weight="fill" />
                  </div>
                  <div>
                    <div className="font-bold text-foreground">{h.name}</div>
                    <div className="text-xs text-muted-foreground">Sobota, {fmtDate(h.swieto_date)}</div>
                  </div>
                </div>
                <button
                  onClick={() => setSelected(selected?.swieto_date === h.swieto_date ? null : { swieto_date: h.swieto_date, name: h.name })}
                  className="px-3 py-1.5 bg-[#2563EB] hover:bg-blue-700 text-white rounded-md text-xs font-semibold"
                  data-testid={`assign-${h.swieto_date}`}
                >
                  {selected?.swieto_date === h.swieto_date ? "Anuluj" : "+ Wyznacz dzień wolny"}
                </button>
              </div>

              {/* Inline form */}
              {selected?.swieto_date === h.swieto_date && (
                <div className="mt-3 pt-3 border-t border-border space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Pracownik</label>
                      <select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-md text-sm bg-white" data-testid={`select-emp-${h.swieto_date}`}>
                        <option value="">— wybierz —</option>
                        {employees.map((e) => (
                          <option key={e._id} value={e._id}>{(e.imie_nazwisko || e.name)} {e.imie_nazwisko ? `(${e.name})` : ""}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Dzień wolny</label>
                      <input type="date" value={form.wolny_date} onChange={(e) => setForm({ ...form, wolny_date: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-md text-sm" data-testid={`select-date-${h.swieto_date}`} />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Data wystawienia dokumentu</label>
                      <input type="date" value={form.data_wystawienia} onChange={(e) => setForm({ ...form, data_wystawienia: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-md text-sm" data-testid={`select-wyst-${h.swieto_date}`} />
                    </div>
                    <div className="flex items-end">
                      <button onClick={addOdbior} disabled={saving}
                        className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-semibold disabled:opacity-50" data-testid={`add-odbior-${h.swieto_date}`}>
                        {saving ? "..." : "Zapisz + generuj PDF"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Assigned odbiory */}
              {h.odbiory && h.odbiory.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                  {h.odbiory.map((o) => (
                    <div key={o.id} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-600" weight="fill" />
                        <span className="font-semibold">{o.employee_name}</span>
                        <span className="text-muted-foreground">— dzień wolny: {fmtDate(o.wolny_date)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => downloadPdf(o.id, o.employee_name, o.swieto_date)}
                          className="p-1.5 text-slate-500 hover:text-blue-600" title="Pobierz PDF" data-testid={`pdf-odbior-${o.id}`}>
                          <Download className="w-4 h-4" />
                        </button>
                        <button onClick={() => removeOdbior(o.id)}
                          className="p-1.5 text-slate-400 hover:text-red-600" title="Usuń" data-testid={`del-odbior-${o.id}`}>
                          <Trash className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
